import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormFieldResponse, RegistrationType } from '@la/types';
import {
  getRegistrationType,
  isPlayerRegistration,
  isStaffRegistration,
} from '@la/utilities';
import { useRegistrationAddOns } from 'redux/hooks/useRegistrationAddOns';
import { clearLocalStorage } from './RegistrationContext.utils';

// Constants
const formSteps: Record<string, string> = {
  'basic-info': 'nonFileUploadFormFields',
  'upload-documents': 'fileUploadFormFields',
};

export type StepInformation = Record<
  string,
  {
    longDesc: string;
    shortDesc: string;
  }
>;

const playerStepInformation: StepInformation = {
  'basic-info': {
    longDesc: 'Provide more info about your player',
    shortDesc: 'Basic information',
  },
  'upload-documents': {
    longDesc: 'Upload required documents',
    shortDesc: 'Upload documents',
  },
  waivers: { longDesc: 'Sign required documents', shortDesc: 'Sign documents' },
  'add-ons': { longDesc: 'Select your add-ons', shortDesc: 'Add-ons' },
  review: {
    longDesc: 'Review your registration',
    shortDesc: 'Review registration',
  },
};

const staffStepInformation: StepInformation = {
  'basic-info': {
    longDesc: 'Provide more information about you and your team',
    shortDesc: 'Basic information',
  },
  'upload-documents': {
    longDesc: 'Upload required documents',
    shortDesc: 'Upload documents',
  },
  waivers: { longDesc: 'Sign required documents', shortDesc: 'Sign documents' },
  'add-ons': { longDesc: 'Select your add-ons', shortDesc: 'Add-ons' },
  review: {
    longDesc: 'Review your registration',
    shortDesc: 'Review registration',
  },
};

const storageKeysToClear = [
  'lip.decodedParams',
  'lip.invite-token',
  'lip.registrationId',
  'selectedPlayer',
];

// Types
type StepConditions = {
  formFields: FormFieldResponse;
  showNGBVerification: boolean;
  includeAddOnsPage: boolean;
  isPlayer: boolean;
};

// Helper functions
function shouldRemoveBasicInfo(
  formFields: FormFieldResponse,
  showNGBVerification: boolean
): boolean {
  return (
    formFields?.nonFileUploadFormFields.length === 0 && !showNGBVerification
  );
}

function shouldRemoveUploadDocuments(formFields: FormFieldResponse): boolean {
  return formFields?.fileUploadFormFields.length === 0;
}

function removeConditionalSteps(
  baseSteps: StepInformation,
  conditions: StepConditions
): StepInformation {
  const { formFields, showNGBVerification, includeAddOnsPage, isPlayer } =
    conditions;
  const steps = { ...baseSteps };

  // Remove basic-info step for staff if no non-file upload fields and no NGB verification
  if (!isPlayer && shouldRemoveBasicInfo(formFields, showNGBVerification)) {
    delete steps['basic-info'];
  }

  // Remove upload-documents step if no file upload fields
  if (shouldRemoveUploadDocuments(formFields)) {
    delete steps['upload-documents'];
  }

  // Remove add-ons step if not included
  if (!includeAddOnsPage) {
    delete steps['add-ons'];
  }

  return steps;
}

function getPathSegment(): string {
  return window.location.pathname.split('/').at(-1) ?? '';
}

function buildRegistrationIdParam(registrationId: string | null): string {
  return registrationId ? `?registrationId=${registrationId}` : '';
}

function determineInitialStep(steps: StepInformation): string {
  if (steps['basic-info']) {
    return 'basic-info';
  }
  if (steps['upload-documents']) {
    return 'upload-documents';
  }
  if (steps['waivers']) {
    return 'waivers';
  }
  if (steps['add-ons']) {
    return 'add-ons';
  }
  return 'review';
}

// Main functions
export function getSteps(
  type: RegistrationType,
  formFields: FormFieldResponse,
  showNGBVerification: boolean,
  includeAddOnsPage: boolean
): StepInformation {
  // Return empty object for unsupported registration types
  if (!isPlayerRegistration(type) && !isStaffRegistration(type)) {
    return {};
  }

  const baseSteps = isPlayerRegistration(type)
    ? playerStepInformation
    : staffStepInformation;

  return removeConditionalSteps(baseSteps, {
    formFields,
    showNGBVerification,
    includeAddOnsPage,
    isPlayer: isPlayerRegistration(type),
  });
}

export function getCurrentStep(
  type: RegistrationType,
  formFields: FormFieldResponse,
  showNGBVerification: boolean,
  showUpdatedECommerceInRegFlowContent: boolean
): string {
  const steps = getSteps(
    type,
    formFields,
    showNGBVerification,
    showUpdatedECommerceInRegFlowContent
  );
  const pathSegment = getPathSegment();

  // Check if we're at the registration root
  if (pathSegment === 'registration' || pathSegment === 'registration/') {
    return determineInitialStep(steps);
  }

  return pathSegment;
}

export const useRegistrantSteps = (
  type: RegistrationType | undefined,
  formFields: FormFieldResponse | undefined,
  showNGBVerification: boolean | undefined,
  tournamentId: string
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationType = getRegistrationType(type);
  const { showUpdatedECommerceInRegFlowContent } = useFlags();
  const { getAddOnsByType } = useRegistrationAddOns();

  const initialRegistrationId = searchParams.get('registrationId');
  const registrationIdParam = buildRegistrationIdParam(initialRegistrationId);
  const includeAddOnsPage =
    (registrationType === 'player' || registrationType === 'staff') &&
    getAddOnsByType(registrationType).length > 0 &&
    showUpdatedECommerceInRegFlowContent;

  // Validate required parameters
  if (!type || !formFields) {
    throw new Error('No type and form fields were provided');
  }

  // Get steps and current step
  const steps = getSteps(
    type,
    formFields,
    !!showNGBVerification,
    includeAddOnsPage
  );

  const currentStep = getCurrentStep(
    type,
    formFields,
    !!showNGBVerification,
    includeAddOnsPage
  );

  // Calculate step numbers
  const stepKeys = Object.keys(steps);
  const stepNumber = stepKeys.findIndex((key) => key === currentStep) + 1;
  const numberOfTotalSteps = stepKeys.length;

  // Navigation helpers
  function getNextStep(): string {
    if (stepNumber >= numberOfTotalSteps) {
      return 'success';
    }
    return stepKeys[stepNumber] ?? 'success';
  }

  function getPreviousStep(): string | undefined {
    if (currentStep === 'success') {
      return 'review';
    }
    return stepKeys[stepNumber - 2];
  }

  // Handle initial navigation for staff registration
  useEffect(() => {
    const pathSegment = getPathSegment();

    // Only handle initial redirection for staff registration at root
    if (!isStaffRegistration(type) || pathSegment !== 'registration') {
      return;
    }

    // Skip basic-info if it doesn't exist for staff registration
    if (!steps['basic-info']) {
      const initialStep = determineInitialStep(steps);
      const targetPath =
        initialStep === 'basic-info'
          ? `/app/registration${registrationIdParam}`
          : `/app/registration/${initialStep}${registrationIdParam}`;

      navigate(targetPath);
    }
  }, [navigate, steps, type, registrationIdParam]);

  // Navigation handlers
  function onNextClick() {
    const nextStep = getNextStep();
    navigate(`/app/registration/${nextStep}${registrationIdParam}`);
  }

  function onBackClick() {
    const previousStep = getPreviousStep();

    if (!previousStep) {
      clearLocalStorage(storageKeysToClear);
      window.location.assign(
        `/app/tournaments/${tournamentId}/select-registration-type`
      );
      return;
    }

    const targetPath =
      previousStep === 'basic-info'
        ? `/app/registration${registrationIdParam}`
        : `/app/registration/${previousStep}${registrationIdParam}`;

    navigate(targetPath);
  }

  return {
    currentStep,
    getNextStep,
    getPreviousStep,
    formSteps,
    numberOfTotalSteps,
    onBackClick,
    onNextClick,
    stepNumber,
    steps,
  };
};
